import React, { useState, useEffect, useCallback } from "react";
import loaderGif from "../../Assests/loader.gif";
import { PreTicketColumns } from "../../ColumnsConfig/PreTicketColumns";
import Table from "../../Components/Table";
import PreTicketService from "../../Services/PreTicketService";
import { Modal, Box, TextField, Button, Typography } from "@mui/material";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";

const INITIAL_TABLE_STATE = {
  rows: [],
  totalRecords: 0,
  currentPage: 1,
  pageSize: 50,
  sortParams: { column: "po_number", order: "asc" },
};

const PreTicket = () => {
  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editedRowData, setEditedRowData] = useState({
    fedex_number: "",
    freight: "",
    ticket_cost: "",
  });
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "po_number",
    direction: "asc",
  });

  // Sorting function
  const sortData = (data, key, direction) => {
    if (!Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (aVal == null) return direction === "asc" ? -1 : 1;
      if (bVal == null) return direction === "asc" ? 1 : -1;

      if (key === "cancel_date") {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }

      if (key === "ticket_cost" || key === "freight") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = useCallback(
    (column, direction) => {
      setSortConfig({ key: column, direction });
      setTableState((prev) => ({
        ...prev,
        sortParams: { column, order: direction },
      }));
      // Trigger data refetch with new sort parameters
      // fetchPreTicketData(currentPage, pageSize);
    },
    [currentPage, pageSize]
  );

  // Data fetching
  const fetchPreTicketData = async (
    page = currentPage,
    size = pageSize,
    poNumbers = "",
    sortBy = tableState.sortParams.column,
    order = tableState.sortParams.order
  ) => {
    console.log("Fetching data with params:", {
      page,
      size,
      poNumbers,
      sortBy,
      order,
    });
    setIsLoading(true);
    try {
      const response = await PreTicketService.fetchPreTicket(
        page,
        size,
        poNumbers,
        sortBy,
        order
      );
      console.log("API Response:", response);

      if (Array.isArray(response.data)) {
        const dataWithIds = response.data.map((item, index) => ({
          ...item,
          id: item.po_number || `row-${index}`,
          cancel_date: item.cancel_date
            ? item.cancel_date.split("T")[0]
            : item.cancel_date,
        }));

        const sortedData = sortData(
          dataWithIds,
          sortConfig.key,
          sortConfig.direction
        );
        setRows(sortedData);
        setFilteredRows(sortedData);
        setTotalRecords(response.totalRecords || dataWithIds.length);
        setTotalPages(
          response.totalPages || Math.ceil(dataWithIds.length / size)
        );

        setTableState((prev) => ({
          ...prev,
          rows: sortedData,
          totalRecords: response.totalRecords || dataWithIds.length,
          currentPage: page,
          pageSize: size,
        }));
      }
    } catch (error) {
      console.error("API Error:", error);
      const errorMessage =
        error.response?.data?.ErrorMessage || "Failed to fetch PreTicket data.";
      NotificationManager.error(errorMessage, "Error", 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load and refresh on dependencies change
  useEffect(() => {
    console.log("UseEffect triggered with:", {
      currentPage,
      pageSize,
      tableState,
    });
    fetchPreTicketData(currentPage, pageSize);
  }, [currentPage, pageSize, sortConfig, tableState.sortParams]);

  // Pagination handler
  const handlePageChange = (page, size) => {
    console.log("Page change:", { page, size });
    setCurrentPage(page);
    setPageSize(size);
    fetchPreTicketData(page, size);
  };

  // Search handlers
  const handleSearch = async () => {
    const poNumbers = searchTerm
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join(",");

    console.log("Searching with PO numbers:", poNumbers);
    await fetchPreTicketData(1, pageSize, poNumbers);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    fetchPreTicketData(1, pageSize);
  };

  // Edit handlers
  const handleEdit = (row) => {
    setEditingRow(row);
    setEditedRowData({
      fedex_number: row.fedex_number || "",
      freight: row.freight || "",
      ticket_cost: row.ticket_cost || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!editingRow) return;

    try {
      const updatedPayload = {
        fedex_number: editedRowData.fedex_number,
        freight: editedRowData.freight,
        ticket_cost: editedRowData.ticket_cost,
      };

      await PreTicketService.updatePreTicket(
        editingRow.po_number,
        updatedPayload
      );

      const updatedRows = filteredRows.map((item) =>
        item.po_number === editingRow.po_number
          ? { ...item, ...updatedPayload }
          : item
      );

      const sortedUpdatedRows = sortData(
        updatedRows,
        sortConfig.key,
        sortConfig.direction
      );
      setFilteredRows(sortedUpdatedRows);

      setEditingRow(null);
      setEditedRowData({
        fedex_number: "",
        freight: "",
        ticket_cost: "",
      });
      setOpen(false);

      NotificationManager.success(
        "PreTicket details updated successfully.",
        "Success",
        3000
      );

      // Refresh data after update
      fetchPreTicketData(currentPage, pageSize);
    } catch (error) {
      console.error("Save Error:", error);
      const errorMessage =
        error.response?.data?.ErrorMessage || "Failed to save changes.";
      NotificationManager.error(errorMessage, "Error", 5000);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingRow(null);
    setEditedRowData({
      fedex_number: "",
      freight: "",
      ticket_cost: "",
    });
  };

  // Rest of the component remains the same...
  return (
    <div className="px-5 my-1">
      <Typography variant="h5" gutterBottom>
        Manage PreTickets
      </Typography>
      <Typography variant="body1" gutterBottom>
        A total of 10 purchase orders can be searched at one time. Please enter
        one PO per line by typing in the PO and hitting the enter key. Then
        click the Search button to return those POs. Click on the PO Number to
        see the details of that PO and update the FedEx number.
      </Typography>

      <TextField
        fullWidth
        label="Search PO Number"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        margin="normal"
        multiline
        rows={4}
        sx={{
          width: "300px",
          "& .MuiInputBase-root": {
            padding: "10px",
          },
        }}
      />

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          onClick={handleSearch}
          variant="contained"
          sx={{ backgroundColor: "#3f506b" }}
        >
          Search
        </Button>
        <Button
          onClick={handleClearSearch}
          variant="contained"
          sx={{ backgroundColor: "#3f506b" }}
        >
          Clear Search
        </Button>
      </Box>

      <div style={{ marginTop: "16px", position: "relative" }}>
        <Table
          rows={filteredRows}
          columns={PreTicketColumns(handleEdit)}
          onPageChange={handlePageChange}
          onSort={handleSort}
          className="table-header"
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          getRowId={(row) => row.po_number}
          sortConfig={sortConfig}
        />

        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            zIndex={1}
          >
            <img src={loaderGif} alt="Loading..." width={50} height={50} />
          </Box>
        )}
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="edit-modal-title"
        aria-describedby="edit-modal-description"
      >
        <Box
          sx={{
            padding: 4,
            maxWidth: 900,
            margin: "auto",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            marginTop: "150px",
            marginBottom: "150px",
          }}
        >
          <h3>Edit PO:</h3>
          <div>
            <TextField
              fullWidth
              label="PO Number"
              value={editingRow?.po_number}
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth
              label="Vendor"
              value={editingRow?.vendor_name}
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth
              label="Cancel Date"
              value={editingRow?.cancel_date}
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth
              label="Division"
              value={editingRow?.chain}
              InputProps={{
                readOnly: true,
              }}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth
              optional
              label="FedEx Number"
              value={editedRowData.fedex_number}
              onChange={(e) =>
                setEditedRowData({
                  ...editedRowData,
                  fedex_number: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              optional
              label="Freight"
              value={editedRowData.freight}
              onChange={(e) =>
                setEditedRowData({
                  ...editedRowData,
                  freight: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Total Cost"
              value={editedRowData.ticket_cost}
              onChange={(e) =>
                setEditedRowData({
                  ...editedRowData,
                  ticket_cost: e.target.value,
                })
              }
              margin="normal"
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Button
                onClick={handleClose}
                variant="contained"
                sx={{ backgroundColor: "#3f506b" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{ backgroundColor: "#3f506b" }}
              >
                Save
              </Button>
            </Box>
          </div>
        </Box>
      </Modal>

      <NotificationContainer />
    </div>
  );
};

export default PreTicket;
